const savedEditKey = "iki-saved-edit-v1";
const legacyStoriesKey = "iki-saved-stories";
const savedEditLimit = 50;

export function normaliseSavedItem(value) {
  if (!value || typeof value !== "object") return null;
  const { kind, id, title, url } = value;
  if (!["product", "article"].includes(kind) || typeof id !== "string" || !/^\d{1,24}$/.test(id)) return null;
  if (typeof title !== "string" || !title.trim() || title.length > 240 || /[\u0000-\u001f]/.test(title)) return null;
  if (typeof url !== "string" || url.length > 500) return null;
  const route = kind === "product"
    ? /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*$/i
    : /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?blogs\/[a-z0-9][a-z0-9-]*\/[a-z0-9][a-z0-9-]*$/i;
  if (!route.test(url)) return null;
  return { kind, id, title: title.trim(), url };
}

export function readSavedItems(storage) {
  const raw = storage.getItem(savedEditKey);
  if (raw === null) return [];
  if (raw.length > 60000) throw new Error("invalid_saved_edit");
  const parsed = JSON.parse(raw);
  if (parsed?.version !== 1 || !Array.isArray(parsed.items) || parsed.items.length > savedEditLimit) throw new Error("invalid_saved_edit");
  const seen = new Set();
  return parsed.items.map(normaliseSavedItem).filter((item) => {
    if (!item || seen.has(`${item.kind}:${item.id}`)) return false;
    seen.add(`${item.kind}:${item.id}`);
    return true;
  });
}

export function writeSavedItems(storage, items) {
  if (items.length > savedEditLimit || items.some((item) => !normaliseSavedItem(item))) throw new Error("invalid_saved_edit");
  storage.setItem(savedEditKey, JSON.stringify({ version: 1, items: items.map(normaliseSavedItem) }));
}

export function removeLegacyStory(storage, id) {
  const raw = storage.getItem(legacyStoriesKey) || "[]";
  let legacy;
  try { legacy = JSON.parse(raw); }
  catch { return; }
  if (Array.isArray(legacy) && legacy.includes(id)) storage.setItem(legacyStoriesKey, JSON.stringify(legacy.filter((key) => key !== id)));
}

function initialiseSavedEdit() {
  const config = document.querySelector("[data-saved-edit-text]");
  if (!config) return;
  const text = JSON.parse(config.textContent);
  const page = document.querySelector("[data-saved-edit]");
  const buttons = () => [...document.querySelectorAll("[data-save-item]")];
  const itemFor = (button) => normaliseSavedItem({ kind: button.dataset.saveKind, id: button.dataset.saveId, url: button.dataset.saveUrl, title: button.dataset.saveTitle });
  const matches = (a, b) => a.kind === b.kind && a.id === b.id;
  const status = page?.querySelector("[data-saved-status]");
  const say = (message, button) => {
    const targets = status ? [status] : button ? [button.closest("[data-save-control]")?.querySelector("[data-save-status]")] : [...document.querySelectorAll("[data-save-status]")];
    targets.filter(Boolean).forEach((target) => { target.textContent = message; target.hidden = false; });
  };

  function render() {
    let items;
    try { items = readSavedItems(localStorage); }
    catch {
      buttons().forEach((button) => { button.hidden = false; });
      if (page) {
        page.querySelector("[data-saved-clear]").hidden = false;
        page.querySelector("[data-saved-list]").replaceChildren();
        page.querySelector("[data-saved-empty]").hidden = true;
      }
      say(text.unavailable);
      return;
    }
    buttons().forEach((button) => {
      const item = itemFor(button);
      if (!item) return;
      const active = items.some((saved) => matches(saved, item));
      button.hidden = false;
      button.setAttribute("aria-pressed", String(active));
      button.textContent = active ? text.saved : text.save;
      button.setAttribute("aria-label", `${active ? text.saved : text.save}: ${item.title}`);
    });
    if (!page) return;
    const list = page.querySelector("[data-saved-list]");
    list.replaceChildren();
    items.forEach((item) => {
      const row = document.createElement("li");
      const link = document.createElement("a");
      const kind = document.createElement("small");
      kind.textContent = item.kind === "article" ? text.article : text.product;
      link.href = item.url;
      link.append(kind, document.createTextNode(item.title));
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "text-link";
      remove.textContent = text.remove;
      remove.setAttribute("aria-label", `${text.remove}: ${item.title}`);
      remove.dataset.savedRemove = `${item.kind}:${item.id}`;
      row.append(link, remove);
      list.append(row);
    });
    page.querySelector("[data-saved-empty]").hidden = items.length !== 0;
    page.querySelector("[data-saved-clear]").hidden = items.length === 0;
  }

  function migrateVisitedStories() {
    try {
      const legacy = JSON.parse(localStorage.getItem(legacyStoriesKey) || "[]");
      if (!Array.isArray(legacy)) return;
      const items = readSavedItems(localStorage);
      let changed = false;
      buttons().forEach((button) => {
        const item = itemFor(button);
        if (item?.kind !== "article" || !legacy.includes(item.id) || items.some((saved) => matches(saved, item)) || items.length >= savedEditLimit) return;
        items.push(item);
        changed = true;
      });
      if (changed) writeSavedItems(localStorage, items);
    } catch { /* The existing saved list stays intact if storage is unavailable. */ }
  }

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-save-item]");
    const remove = event.target.closest("[data-saved-remove]");
    const clear = event.target.closest("[data-saved-clear]");
    if (!button && !remove && !clear) return;
    try {
      if (clear) {
        localStorage.removeItem(legacyStoriesKey);
        localStorage.removeItem(savedEditKey);
        render();
        say(text.cleared);
        status?.focus();
        return;
      }
      const items = readSavedItems(localStorage);
      const item = button ? itemFor(button) : items.find((saved) => `${saved.kind}:${saved.id}` === remove.dataset.savedRemove);
      if (!item) return;
      const exists = items.some((saved) => matches(saved, item));
      if (!exists && items.length >= savedEditLimit) { say(text.full, button); return; }
      if (exists && item.kind === "article") removeLegacyStory(localStorage, item.id);
      const next = exists ? items.filter((saved) => !matches(saved, item)) : [...items, item];
      writeSavedItems(localStorage, next);
      const rowIndex = remove ? [...page.querySelectorAll("[data-saved-remove]")].indexOf(remove) : -1;
      render();
      say(exists ? text.removed : text.added, button);
      if (remove) {
        const remaining = [...page.querySelectorAll("[data-saved-remove]")];
        (remaining[Math.min(rowIndex, remaining.length - 1)] || status)?.focus();
      }
    } catch { say(text.unavailable, button); }
  });
  window.addEventListener("storage", (event) => { if (event.key === savedEditKey || event.key === null) render(); });
  window.addEventListener("pageshow", render);
  document.addEventListener("shopify:section:load", () => { migrateVisitedStories(); render(); });
  migrateVisitedStories();
  render();
}

if (typeof document !== "undefined") initialiseSavedEdit();
